import { ContextApi } from 'contexts/Localization/types'
import { QuestSymbol, QuestCategory, QuestType, QuestFrequency, Rarity, Tier, FoodType } from 'state/vvsgotchi/models'
import { IBaseError as UpdateMoleError, ErrorCode as UpdateMoleErrorCode } from 'vvs-gotchi-graphql'
import {
  QuestSymbolToImageUrl,
  QuestSymbolToTypePostfix,
  QuestCategoryToUiDetails,
  QuestTypeToName,
  QuestFrequencyText,
  RarityToName,
  RarityTierMatrix,
} from './Quest/types'
import { TierToIcon, TierToName, TierToSize } from './Team/types'
import { FoodTypeToUiDetails } from './FeedMole/types'

export const MAX_TEAM_SIZE = 5

export const MAX_MOLE_NAME_LENGTH = 8
export const MIN_MOLE_NAME_LENGTH = 3
export const MIN_MOLE_NAME_INVALID = /[^A-Za-z0-9]/

export const tierToIcon: TierToIcon = {
  [Tier.JUNIOR]: '/images/vvsGotchi/tier/junior.png',
  [Tier.MASTER]: '/images/vvsGotchi/tier/master.png',
  [Tier.GOD]: '/images/vvsGotchi/tier/god.png',
}

export const tierToName: TierToName = {
  [Tier.JUNIOR]: (t) => ({ full: t('Junior Miner'), short: t('Junior') }),
  [Tier.MASTER]: (t) => ({ full: t('Miner Master'), short: t('Master') }),
  [Tier.GOD]: (t) => ({ full: t('Miner God'), short: t('God') }),
}

export const tierToIconSize: TierToSize = {
  [Tier.JUNIOR]: { sm: '14px', md: '24px' },
  [Tier.MASTER]: { sm: '14px', md: '26px' },
  [Tier.GOD]: { sm: '20px', md: '30px' },
}
export const tierRangeConfig: Record<Tier, (t: ContextApi['t']) => string> = {
  [Tier.JUNIOR]: (t) => t('Bottom %percent%%', { percent: 30 }),
  [Tier.MASTER]: (t) => t('Middle'),
  [Tier.GOD]: (t) => t('Top %percent%%', { percent: 30 }),
}

export const questCategoryDetails: QuestCategoryToUiDetails = {
  [QuestCategory.MOLE_CARE]: {
    title: (t) => t('Mole Care'),
    description: (t) => t('Hardworking Moles need pampering!'),
    bannerImageUrl: '/images/vvsGotchi/quest_category/mole_care_banner.png',
    bgImageUrl: '/images/vvsGotchi/bg_violet_red.png',
  },
  [QuestCategory.DAILY_WORK]: {
    title: (t) => t('Daily Work'),
    description: (t) => t('The secret to success is to go on daily quests!'),
    bannerImageUrl: '/images/vvsGotchi/quest_category/daily_work_banner.png',
    bgImageUrl: '/images/vvsGotchi/bg_mint.png',
  },
  [QuestCategory.SPECIAL_MISSION]: {
    title: (t) => t('Special'),
    description: (t) => t('Sometimes Mole is sent to aboard on temporary mission'),
    bannerImageUrl: '/images/vvsGotchi/quest_category/special_banner.png',
    bgImageUrl: '/images/vvsGotchi/bg_ocean.png',
  },
  [QuestCategory.GAME_CHALLENGES]: {
    title: (t) => t('Challenges'),
    description: (t) => t('Mole life is never complete without its Challenges!'),
    bgImageUrl: '/images/vvsGotchi/bg_violet_red.png',
  },
}
export const questTypeToName: QuestTypeToName = {
  [QuestType.FARM]: (t) => t('Farm'),
  [QuestType.FLASH_LP]: (t) => t('Flash LP'),
  [QuestType.IGO]: (t) => t('IGO'),
  [QuestType.LIMIT_ORDER]: (t) => t('Limit Order'),
  [QuestType.LIQUIDITY]: (t) => t('Liquidity'),
  [QuestType.SWAP]: (t) => t('Swap'),
  [QuestType.XVVS]: (t) => t('xVVS'),
}
export const questSymbolToTypePostfix: QuestSymbolToTypePostfix = {
  [QuestSymbol.MOLE_OPOLY]: (t) => t(' (4 years vault)'),
}
export const questSymbolToImageUrl: QuestSymbolToImageUrl = {
  [QuestSymbol.ADOPT]: '/images/vvsGotchi/quest/adopt.png',
  [QuestSymbol.TEAM_UP]: '/images/vvsGotchi/quest/team_up.png',
  [QuestSymbol.BARTER]: '/images/vvsGotchi/quest/barter.png',
  [QuestSymbol.MOLE_OPOLY]: '/images/vvsGotchi/quest/mole_opoly.png',
  [QuestSymbol.HYDRATE]: '/images/vvsGotchi/quest/hydrate.png',
  [QuestSymbol.PROFIT]: '/images/vvsGotchi/quest/profit.png',
  [QuestSymbol.SOCIALISE]: '/images/vvsGotchi/quest/socialise.png',
  [QuestSymbol.FARM]: '/images/vvsGotchi/quest/farm.png',
  [QuestSymbol.GOLD_DIGGER]: '/images/vvsGotchi/quest/gold_digger.png',
  [QuestSymbol.MOLE_DASH]: '/images/vvsGotchi/quest/mole_dash.png',
}
export const questSymbolToImagePadding: Partial<Record<QuestSymbol, string>> = {
  [QuestSymbol.FARM]: '8px',
  [QuestSymbol.GOLD_DIGGER]: '16px 0',
}

export const questFrequencyText: QuestFrequencyText = {
  [QuestFrequency.ONE_TIME_ONLY]: (t) => t('One time only'),
  [QuestFrequency.EVENT_DRIVEN]: (t) => t('Can do once only per event'),
  [QuestFrequency.UNLIMITED]: (t) => t('Can do unlimited times'),
  [QuestFrequency.ONCE_A_DAY]: (t) => t('Can do once per day'),
}

export const rarityToName: RarityToName = {
  [Rarity.COMMON]: (t) => t('Common'),
  [Rarity.SPECIAL]: (t) => t('Special'),
  [Rarity.RARE]: (t) => t('Rare'),
  [Rarity.EPIC]: (t) => t('Epic'),
  [Rarity.LEGENDARY]: (t) => t('Legendary'),
}

export const rarityTierMatrix: RarityTierMatrix = {
  [Rarity.COMMON]: {
    [Tier.JUNIOR]: {
      boost: 0.05,
      rangeStart: 3904,
      rangeEnd: 5576,
    },
    [Tier.MASTER]: {
      boost: 0.06,
      rangeStart: 1673,
      rangeEnd: 3903,
    },
    [Tier.GOD]: {
      boost: 0.085,
      rangeStart: 1,
      rangeEnd: 1672,
    },
  },
  [Rarity.SPECIAL]: {
    [Tier.JUNIOR]: {
      boost: 0.075,
      rangeStart: 2017,
      rangeEnd: 2880,
    },
    [Tier.MASTER]: {
      boost: 0.085,
      rangeStart: 865,
      rangeEnd: 2016,
    },
    [Tier.GOD]: {
      boost: 0.12,
      rangeStart: 1,
      rangeEnd: 864,
    },
  },
  [Rarity.RARE]: {
    [Tier.JUNIOR]: {
      boost: 0.1,
      rangeStart: 910,
      rangeEnd: 1299,
    },
    [Tier.MASTER]: {
      boost: 0.12,
      rangeStart: 390,
      rangeEnd: 909,
    },
    [Tier.GOD]: {
      boost: 0.19,
      rangeStart: 1,
      rangeEnd: 389,
    },
  },
  [Rarity.EPIC]: {
    [Tier.JUNIOR]: {
      boost: 0.15,
      rangeStart: 130,
      rangeEnd: 185,
    },
    [Tier.MASTER]: {
      boost: 0.19,
      rangeStart: 56,
      rangeEnd: 129,
    },
    [Tier.GOD]: {
      boost: 0.31,
      rangeStart: 1,
      rangeEnd: 55,
    },
  },
  [Rarity.LEGENDARY]: {
    [Tier.JUNIOR]: {
      boost: 0.25,
      rangeStart: 43,
      rangeEnd: 60,
    },
    [Tier.MASTER]: {
      boost: 0.31,
      rangeStart: 19,
      rangeEnd: 42,
    },
    [Tier.GOD]: {
      boost: 0.49,
      rangeStart: 1,
      rangeEnd: 18,
    },
  },
}

export const HEALTH_LEVEL_VALUES = {
  excellent: 100,
  healthy: 71,
  normal: 61,
  unhealthy: 11,
  sick: 0,
}

export const foodTypeToUiDetails: FoodTypeToUiDetails = {
  [FoodType.FRIES]: {
    name: (t) => t('Fries'),
    image: '/images/vvsGotchi/food/fries.png',
    feedingImage: '/images/vvsGotchi/feeding/fries.png',
    molePreference: (t) => t('Loves Fries!'),
    acceptanceChance: 1,
    healthPointsToAdd: 2,
    price: 0,
  },
  [FoodType.BROCCOLI]: {
    name: (t) => t('Broccoli'),
    image: '/images/vvsGotchi/food/broccoli.png',
    feedingImage: '/images/vvsGotchi/feeding/fries.png',
    molePreference: (t) => t('Doesn’t like Broccoli'),
    acceptanceChance: 0.05,
    healthPointsToAdd: 16,
    price: 0,
  },
  [FoodType.GUACAMOLE]: {
    name: (t) => t('Guacamole'),
    image: '/images/vvsGotchi/food/guacamole.png',
    feedingImage: '/images/vvsGotchi/feeding/fries.png',
    molePreference: (t) => t('Not supported'),
    acceptanceChance: 1,
    healthPointsToAdd: 2,
    price: 0,
  },
}

export const getUpdateMoleErrorsMsg = (updateMoleError: UpdateMoleError, t: ContextApi['t']): string => {
  const { code, message } = updateMoleError

  if ([UpdateMoleErrorCode.NameNotAvailable, UpdateMoleErrorCode.NameTaken].includes(code)) {
    return t('Not available! Please choose another')
  }
  return message // unknown errors, too long or short case should be blocked by RenameModal
}
